import { computeMetrics, resolveMacros } from "@/lib/calc";
import { dietTotals, mealTotals } from "@/lib/diet";
import { fmt } from "@/lib/format";
import { ANTROPO_FIELDS, PLICO_FIELDS } from "@/components/lab/MisurazioniTab";
import { planInfo } from "@/lib/plan";

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function metabolicSummary(patient) {
  const m = computeMetrics(patient);
  const g = resolveMacros(patient);
  const rows = [
    ["Sesso", patient.sesso === "donna" ? "Donna" : "Uomo"],
    ["Età", patient.eta ? `${esc(patient.eta)} anni` : "—"],
    ["Peso", patient.peso ? `${fmt(patient.peso)} kg` : "—"],
    ["Altezza", patient.altezza ? `${fmt(patient.altezza)} cm` : "—"],
    ["BMR", `${fmt(m.bmr)} kcal`],
    ["TDEE", `${fmt(m.tdee)} kcal`],
    ["BMI", `${fmt(m.bmi, 1)} (${m.bmiCat})`],
    ["Peso ideale", `${fmt(m.devine, 1)} kg`],
    ["Target calorico", `${fmt(g.kcal)} kcal`],
    ["Macro (C/P/G)", `${fmt(g.carbG)} / ${fmt(g.protG)} / ${fmt(g.fatG)} g`],
  ];
  return `<div class="grid">${rows.map(([k, v]) => `<div class="cell"><span class="k">${esc(k)}</span><span class="v">${esc(v)}</span></div>`).join("")}</div>`;
}

function dietSection(diet) {
  if (!diet) return "";
  const dt = dietTotals(diet);
  const meals = diet.meals
    .map((meal) => {
      const t = mealTotals(meal);
      const foodRows = meal.foods.length
        ? meal.foods
            .map(
              (f) => `<tr>
        <td>${esc(f.nome)}</td>
        <td class="num">${fmt(f.grammi)} ${f.unit === "pezzo" ? "pz" : "g"}</td>
        <td class="num">${fmt(f.kcal)}</td>
        <td class="num">${fmt(f.carbo, 1)}</td>
        <td class="num">${fmt(f.protAnimali + f.protVegetali, 1)}</td>
        <td class="num">${fmt(f.grassi, 1)}</td>
      </tr>`
            )
            .join("")
        : `<tr><td colspan="6" class="empty">Nessun alimento</td></tr>`;
      return `<div class="meal">
        <div class="meal-h">${esc(meal.nome)}</div>
        <table>
          <thead><tr><th>Alimento</th><th class="num">Qtà</th><th class="num">Kcal</th><th class="num">Carbo</th><th class="num">Prot.</th><th class="num">Grassi</th></tr></thead>
          <tbody>${foodRows}</tbody>
          <tfoot><tr><td>Totale pasto</td><td></td><td class="num">${fmt(t.kcal)}</td><td class="num">${fmt(t.carbo, 1)}</td><td class="num">${fmt(t.protAnimali, 1)}</td><td class="num">${fmt(t.grassi, 1)}</td></tr></tfoot>
        </table>
      </div>`;
    })
    .join("");

  return `<h2>Piano Alimentare — ${esc(diet.nome)}</h2>
    ${meals}
    <div class="daily">Totale giornaliero: <b>${fmt(dt.kcal)} kcal</b> · Carbo ${fmt(dt.carbo, 1)} g · Proteine animali ${fmt(dt.protAnimali, 1)} g · Grassi ${fmt(dt.grassi, 1)} g</div>
    ${diet.note ? `<div class="note"><b>Note:</b> ${esc(diet.note)}</div>` : ""}`;
}

const MEASURE_META = {
  antropo: { fields: ANTROPO_FIELDS, unit: "cm", title: "Misurazioni Antropometriche" },
  plico: { fields: PLICO_FIELDS, unit: "mm", title: "Misurazioni Plicometriche" },
};

function measureTable(type, weeks) {
  if (!weeks || !weeks.length) return "";
  const { fields, unit, title } = MEASURE_META[type];
  const head = `<tr><th>Data</th><th class="num">PESO (kg)</th>${fields.map((f) => `<th class="num">${esc(f)}</th>`).join("")}</tr>`;
  const body = weeks.map((w) => {
    const label = w.date ? new Date(w.date).toLocaleDateString("it-IT") : (w.label || "—");
    const peso = w.values && w.values.PESO ? esc(w.values.PESO) : "—";
    return `<tr><td>${esc(label)}</td><td class="num">${peso}</td>${fields.map((f) => `<td class="num">${w.values && w.values[f] ? esc(w.values[f]) : "—"}</td>`).join("")}</tr>`;
  }).join("");
  return `<h2>${title} <span class="unit">(${unit})</span></h2>
    <table><thead>${head}</thead><tbody>${body}</tbody></table>`;
}

function measurementsSection(measurements) {
  if (!measurements) return "";
  const type = measurements.type || "antropo";
  return measureTable(type, measurements[type] || []);
}

function measurementsAll(measurements) {
  if (!measurements) return "";
  return measureTable("antropo", measurements.antropo || []) + measureTable("plico", measurements.plico || []);
}

function photosSection(foto) {
  const sections = (foto && foto.sections) || [];
  const withPhotos = sections.filter((s) => s.photos && Object.keys(s.photos).length);
  if (!withPhotos.length) return "";
  const angles = [["frontale", "Foto Frontali"], ["laterale", "Foto Laterali"], ["dietro", "Foto da Dietro"]];
  const blocks = angles.map(([a, al]) => {
    const cells = withPhotos.filter((s) => s.photos[a]).map((s) => {
      const v = (s.view && s.view[a]) || { zoom: 1, y: 0 };
      const date = s.date ? new Date(s.date).toLocaleDateString("it-IT") : "";
      const cap = [esc(s.label || ""), date].filter(Boolean).join(" · ");
      return `<div class="ph"><div class="ph-box"><img src="${s.photos[a]}" style="transform:translateY(${v.y}%) scale(${v.zoom})"/></div><span>${cap}</span></div>`;
    }).join("");
    if (!cells) return "";
    return `<div class="photo-row"><div class="pr-label">${al}</div><div class="ph-grid">${cells}</div></div>`;
  }).join("");
  if (!blocks) return "";
  return `<h2>Foto — Percorso del Paziente</h2>${blocks}`;
}

const STYLES = `
    * { box-sizing: border-box; }
    body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1a2422; margin: 0; padding: 32px 36px; background: #fff; }
    .top { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #10b981; padding-bottom: 12px; margin-bottom: 20px; }
    .brand { display: flex; align-items: center; gap: 10px; }
    .logo { width: 34px; height: 34px; border-radius: 9px; background: #10b981; color: #fff; display: grid; place-items: center; font-weight: 800; font-size: 18px; }
    .brand h1 { font-size: 18px; margin: 0; }
    .brand p { font-size: 11px; color: #667; margin: 2px 0 0; }
    .meta { text-align: right; font-size: 12px; color: #445; }
    .meta .name { font-size: 16px; font-weight: 700; color: #10312a; }
    h2 { font-size: 14px; margin: 22px 0 8px; color: #0f5132; border-left: 4px solid #10b981; padding-left: 8px; }
    h2 .unit { font-weight: 400; color: #889; font-size: 12px; }
    .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px 24px; }
    .cell { display: flex; justify-content: space-between; border-bottom: 1px dotted #dde; padding: 3px 0; font-size: 12.5px; }
    .cell .k { color: #667; }
    .cell .v { font-weight: 600; }
    .meal { margin-bottom: 12px; page-break-inside: avoid; }
    .meal-h { font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; background: #eafaf3; color: #0f5132; padding: 5px 8px; border-radius: 5px 5px 0 0; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { text-align: left; padding: 5px 8px; border-bottom: 1px solid #eef1f0; }
    th { background: #f6f8f7; font-size: 10.5px; text-transform: uppercase; letter-spacing: .03em; color: #556; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    tfoot td { font-weight: 700; border-top: 2px solid #cfe9dd; background: #fafdfb; }
    .empty { color: #99a; font-style: italic; text-align: center; padding: 8px 0; }
    .daily { margin-top: 8px; padding: 10px 12px; background: #10b981; color: #fff; border-radius: 8px; font-size: 13px; }
    .note { margin-top: 8px; padding: 10px 12px; background: #f6f8f7; border: 1px solid #e3e8e6; border-left: 4px solid #10b981; border-radius: 8px; font-size: 12.5px; color: #33413d; white-space: pre-wrap; }
    .foot { margin-top: 28px; border-top: 1px solid #e3e8e6; padding-top: 10px; font-size: 10.5px; color: #99a; text-align: center; }
    .photo-row { margin: 10px 0 16px; page-break-inside: avoid; }
    .pr-label { font-weight: 700; font-size: 12px; color: #0f5132; margin-bottom: 6px; }
    .ph-grid { display: flex; gap: 12px; flex-wrap: wrap; }
    .ph { text-align: center; }
    .ph-box { width: 120px; aspect-ratio: 9 / 16; overflow: hidden; border-radius: 8px; border: 1px solid #e3e8e6; background: #f6f8f7; }
    .ph-box img { width: 100%; height: 100%; object-fit: cover; }
    .ph span { display: block; font-size: 10px; color: #889; margin-top: 3px; }
    @page { margin: 16mm; }
`;

const nowDate = () => new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
const nameOf = (p) => [p.cognome, p.nome].filter(Boolean).join(" ") || "Paziente";

function buildDoc(title, fullName, date, inner) {
  return `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${esc(fullName)} — ${esc(title)}</title>
  <style>${STYLES}</style></head><body>
    <div class="top">
      <div class="brand"><div class="logo">N</div><div><h1>NutriScience Lab</h1><p>Sistema Clinico di Calcolo Metabolico e Dietoterapia</p></div></div>
      <div class="meta"><div class="name">${esc(fullName)}</div><div>${esc(date)}</div></div>
    </div>
    ${inner}
    <div class="foot">Documento generato da NutriScience Lab — a scopo professionale, non sostituisce il parere medico.</div>
  </body></html>`;
}

function openPrint(html) {
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) { URL.revokeObjectURL(url); return false; }
  w.focus();
  const doPrint = () => { try { w.focus(); w.print(); } catch (e) { console.error("print failed (window likely closed):", e); } };
  setTimeout(doPrint, 700);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
}

function buildFullReportHTML(patient) {
  const pi = planInfo(patient);
  let planHtml = "";
  if (pi.hasPlan) {
    const status = pi.expired ? `Scaduto da ${Math.abs(pi.daysLeft)} giorni` : `${pi.daysLeft} giorni rimanenti`;
    planHtml = `<div class="daily" style="background:#0f5132">Piano nutrizionale: dal ${pi.start.toLocaleDateString("it-IT")} al ${pi.end.toLocaleDateString("it-IT")} · ${status}</div>`;
  }
  const diets = (patient.diets || []).map((d) => dietSection(d)).join("");
  const inner = `
    ${planHtml}
    <h2>Sezione 1 — Metabolismo &amp; Macro</h2>
    ${metabolicSummary(patient)}
    <h2>Sezione 3 — Piano Alimentare</h2>
    ${diets || '<div class="empty">Nessuna dieta registrata</div>'}
    <h2>Sezione 5 — Misurazioni</h2>
    ${measurementsAll(patient.measurements) || '<div class="empty">Nessuna misurazione registrata</div>'}
    <h2>Sezione 7 — Foto</h2>
    ${photosSection(patient.foto) || '<div class="empty">Nessuna foto caricata</div>'}`;
  return buildDoc("Report Percorso Cliente", nameOf(patient), nowDate(), inner);
}

function slugName(patient) {
  return (nameOf(patient) || "paziente").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "paziente";
}

// Share an HTML report file via the Web Share API, with download fallback.
async function shareHTML(html, filename, title, text) {
  const file = new File([html], filename, { type: "text/html" });
  if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "cancelled";
    }
  }
  // Fallback: download the file so it can be attached/sent manually
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return "downloaded";
}

export function exportFullReport(patient) {
  return openPrint(buildFullReportHTML(patient));
}

export function shareFullReport(patient) {
  return shareHTML(
    buildFullReportHTML(patient),
    `report-${slugName(patient)}.html`,
    "Report Percorso Cliente",
    `Report del percorso di ${nameOf(patient)} — NutriScience Lab`
  );
}

function progTable(name, entries) {
  const list = [...(entries || [])].sort((a, b) => String(a.date || a.created_at || "").localeCompare(String(b.date || b.created_at || "")));
  if (!list.length) return `<div class="pex"><div class="pname">${esc(name)}</div><div class="empty">Nessuna progressione registrata</div></div>`;
  const n = Math.max(...list.map((e) => (e.sets || [{ kg: e.kg, reps: e.reps }]).length));
  const head = Array.from({ length: n }, (_, i) => `<th>Serie ${i + 1}</th>`).join("");
  const rows = list.map((e) => {
    const sets = e.sets || [{ kg: e.kg, reps: e.reps }];
    const d = e.date ? new Date(e.date).toLocaleDateString("it-IT") : "—";
    const cells = Array.from({ length: n }, (_, i) => { const s = sets[i]; return `<td>${s && (s.kg || s.reps) ? `${esc(s.kg || "—")} kg × ${esc(s.reps || "—")}` : "—"}</td>`; }).join("");
    return `<tr><td>${d}</td>${cells}<td class="${e.confirmed ? "ok" : "wait"}">${e.confirmed ? "Confermata" : "Da confermare"}</td></tr>`;
  }).join("");
  return `<div class="pex"><div class="pname">${esc(name)}</div><table><thead><tr><th>Data</th>${head}<th>Stato</th></tr></thead><tbody>${rows}</tbody></table></div>`;
}

export function exportProgressionsPDF(patientName, allenamento, progress) {
  const schede = (allenamento && allenamento.schede) || [];
  const inner = schede.map((s, i) => {
    const letter = String.fromCharCode(65 + i);
    const ex = (s.esercizi || []).filter((e) => !e.cardio).map((e) => (e.superset
      ? progTable(`A · ${(e.a && e.a.nome) || "Esercizio A"}`, progress[`${e.id}:a`]) + progTable(`B · ${(e.b && e.b.nome) || "Esercizio B"}`, progress[`${e.id}:b`])
      : progTable(e.nome || "Esercizio", progress[e.id]))).join("");
    return `<div class="pscheda"${i ? ' style="page-break-before:always"' : ""}><h2>Scheda ${letter}${s.gruppo ? ` — ${esc(s.gruppo.toUpperCase())}` : ""}</h2>${ex || '<div class="empty">Nessun esercizio</div>'}</div>`;
  }).join("") || '<div class="empty">Nessuna scheda di allenamento</div>';
  const css = `<style>.pex{margin:10px 0 16px;break-inside:avoid}.pname{font-weight:800;font-size:13px;margin-bottom:6px;color:#0f5132}.pex table{width:100%;border-collapse:collapse;font-size:11px}.pex th,.pex td{border:1px solid #d9e2dc;padding:5px 7px;text-align:left}.pex th{background:#eef5f0}.pex td.ok{color:#0f5132;font-weight:700}.pex td.wait{color:#b45309;font-weight:700}</style>`;
  return openPrint(buildDoc("Progressioni Allenamento", patientName || "Paziente", nowDate(), css + inner));
}


const PRIV_Q = [
  ["letto", "Dichiaro di aver letto l'Informativa Privacy."],
  ["servizio", "Acconsento al trattamento dei miei dati personali per l'erogazione del servizio di coaching online."],
  ["salute", "Acconsento al trattamento delle informazioni relative al mio stato di salute, delle misure corporee e dei dati necessari alla personalizzazione del percorso."],
  ["foto", "Acconsento al trattamento delle fotografie inviate durante il percorso esclusivamente per il monitoraggio dei progressi."],
];
const PRIV_TEXT = "Ai sensi del Regolamento (UE) 2016/679 (GDPR), i dati personali conferiti saranno trattati esclusivamente per finalità connesse all'erogazione del servizio di coaching online. Potranno essere raccolti: dati anagrafici, recapiti, peso, altezza, misure corporee, fotografie, dati relativi alle abitudini alimentari, dati relativi all'attività fisica, eventuali informazioni riguardanti limitazioni fisiche, infortuni o altre condizioni comunicate spontaneamente dall'utente. Le fotografie saranno utilizzate esclusivamente per confrontare i progressi durante il percorso. I dati saranno conservati con adeguate misure di sicurezza e non saranno comunicati a terzi salvo obblighi di legge. L'interessato potrà esercitare in qualsiasi momento i diritti previsti dagli artt. 15-22 del GDPR.";

export function exportPrivacyPDF(r) {
  const d = (v) => (v ? new Date(v).toLocaleDateString("it-IT") : "—");
  const row = (k, v) => `<tr><th style="width:38%">${esc(k)}</th><td>${esc(v || "—")}</td></tr>`;
  const inner = `<h2>Consenso al trattamento dei dati personali — Coaching Online AGFit</h2>
    <p style="font-size:11px;line-height:1.5">${esc(PRIV_TEXT)}</p>
    <h2>Dati dell'interessato</h2>
    <table class="ptab">${row("Nome e Cognome", r.nome_cognome)}${row("Data di nascita", d(r.data_nascita))}${row("Luogo di nascita", r.luogo_nascita)}${row("Telefono", r.telefono)}${row("Email", r.email)}</table>
    <h2>Consensi</h2>
    <table class="ptab">${PRIV_Q.map(([k, q]) => row(q, r[k])).join("")}
    ${row("Autorizzo AGFit a pubblicare fotografie e/o testimonianze relative ai miei risultati", r.pubblicazione || "Non indicato")}
    ${r.pubblicazione === "Si" ? row("Autorizzazioni", (r.autorizzazioni || []).join(", ") || "Nessuna") : ""}</table>
    <h2>Dichiarazioni</h2>
    <ul style="font-size:12px">${(r.dichiarazioni || []).map((x) => `<li>✔ ${esc(x)}</li>`).join("")}</ul>
    <h2>Firma</h2>
    <table class="ptab">${row("Nome e Cognome", r.firma_nome)}${row("Luogo", r.firma_luogo)}${row("Data", d(r.firma_data))}${row("Inviato dal link personale il", new Date(r.submitted_at).toLocaleString("it-IT"))}</table>
    <style>.ptab{width:100%;border-collapse:collapse;font-size:12px;margin:6px 0 14px}.ptab th,.ptab td{border:1px solid #d9e2dc;padding:6px 8px;text-align:left;vertical-align:top}.ptab th{background:#eef5f0;font-weight:600}</style>`;
  return openPrint(buildDoc("Modulo Privacy", r.nome_cognome || "Paziente", d(r.submitted_at), inner));
}
